'use client'

import { useState, useEffect, useMemo } from 'react'

import { Check, ChevronsUpDown, X, Folder, Tag } from 'lucide-react'

import permissionService, {
	PERMISSIONS,
} from '@/core/api/services/permissionService'
import { useContentCategories } from '@/core/hooks/misc/useContentCategories'
import { useResellers } from '@/core/hooks/useReseller'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Badge } from '@/shared/components/atoms/Badge/Badge'
import { Button } from '@/shared/components/atoms/Button'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/atoms/Command'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/atoms/Popover'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import { Separator } from '@/shared/components/atoms/Separator'
import { Switch } from '@/shared/components/atoms/Switch'
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/atoms/Tabs'
import { Textarea } from '@/shared/components/atoms/TextArea'
import {
	BottomSheet,
	BottomSheetContent,
} from '@/shared/components/molecules/BottomSheet/BottomSheet'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { cn } from '@/shared/utils/helpers'

import {
	SUPPORTED_SCHEDULE_LANGUAGES,
	type ScheduleLanguageCode,
	DEFAULT_SCHEDULE_LANGUAGE,
} from '../constants/languages'

interface EditScheduleModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	initialTitle: string
	initialDescription: string
	initialTitleTranslations?: Record<string, string>
	initialDescriptionTranslations?: Record<string, string>
	initialPublic?: boolean
	initialContentCategoryIds?: number[]
	initialResellerId?: number | null
	onSave: (
		title: string,
		description: string,
		titleTranslations: Record<string, string>,
		descriptionTranslations: Record<string, string>,
		isPublic?: boolean,
		contentCategoryIds?: number[],
		resellerId?: number | null
	) => Promise<void>
}

export const EditScheduleModal = ({
	open,
	onOpenChange,
	initialTitle,
	initialDescription,
	initialTitleTranslations,
	initialDescriptionTranslations,
	initialPublic = false,
	initialContentCategoryIds = [],
	initialResellerId = null,
	onSave,
}: EditScheduleModalProps) => {
	const { t } = useTranslation()
	const [selectedLanguage, setSelectedLanguage] =
		useState<ScheduleLanguageCode>(DEFAULT_SCHEDULE_LANGUAGE)
	const [titleTranslations, setTitleTranslations] = useState<
		Record<string, string>
	>({})
	const [descriptionTranslations, setDescriptionTranslations] = useState<
		Record<string, string>
	>({})
	const [isPublic, setIsPublic] = useState(initialPublic)
	const [contentCategoryIds, setContentCategoryIds] = useState<number[]>(
		initialContentCategoryIds
	)
	const [isSaving, setIsSaving] = useState(false)
	const [isDesktop, setIsDesktop] = useState(
		() => !window.matchMedia('(max-width: 767px)').matches
	)
	const [isCategoryPopoverOpen, setIsCategoryPopoverOpen] = useState(false)
	const [resellerId, setResellerId] = useState<number | null>(initialResellerId)
	const [isResellerPopoverOpen, setIsResellerPopoverOpen] = useState(false)
	const [resellerSearch, setResellerSearch] = useState('')
	const debouncedResellerSearch = useDebounce(resellerSearch, 400)

	const { data: allContentCategories = [] } = useContentCategories({
		options: { enabled: open },
	})
	const contentCategories = allContentCategories.filter(
		(c) => c.showInSchedules
	)

	const canManagePublic = permissionService.currentUserHasAllPermissions([
		PERMISSIONS.CREATE_SCHEDULES,
		PERMISSIONS.UPDATE_SCHEDULES,
	])

	const { data: resellersData, isLoading: isResellersLoading } = useResellers({
		config: {
			params: {
				per_page: 50,
				...(debouncedResellerSearch ? { query: debouncedResellerSearch } : {}),
			},
		},
		options: {
			enabled: open && canManagePublic,
			cacheTime: 60 * 60 * 1000,
			staleTime: 60 * 60 * 1000,
		},
	})
	const resellers = resellersData?.data ?? []
	const selectedReseller = resellers.find((r) => r.id === resellerId)

	// Users with these permissions get extended description limit (1000 chars instead of 180)
	const descriptionMaxLength = canManagePublic ? 1000 : 180

	useEffect(() => {
		const mediaQuery = window.matchMedia('(max-width: 767px)')
		const handler = (e: MediaQueryListEvent) => setIsDesktop(!e.matches)
		mediaQuery.addEventListener('change', handler)
		return () => mediaQuery.removeEventListener('change', handler)
	}, [])

	useEffect(() => {
		if (open) {
			// Initialize translations from props
			if (initialTitleTranslations) {
				setTitleTranslations(initialTitleTranslations)
			} else {
				// Fallback to initialTitle for backward compatibility
				setTitleTranslations({ 'en-US': initialTitle })
			}

			if (initialDescriptionTranslations) {
				setDescriptionTranslations(initialDescriptionTranslations)
			} else {
				// Fallback to initialDescription for backward compatibility
				setDescriptionTranslations({ 'en-US': initialDescription })
			}

			setIsPublic(initialPublic)
			setContentCategoryIds(initialContentCategoryIds)
			setResellerId(initialResellerId)
			setResellerSearch('')
		}
	}, [
		open,
		initialTitle,
		initialDescription,
		initialTitleTranslations,
		initialDescriptionTranslations,
		initialPublic,
		initialContentCategoryIds,
		initialResellerId,
	])

	// Check if any language has a title
	const hasAtLeastOneTitle = useMemo(() => {
		return Object.values(titleTranslations).some((title) => title.trim())
	}, [titleTranslations])

	// Check which languages have content
	const languageHasContent = useMemo(() => {
		const result: Record<string, boolean> = {}
		SUPPORTED_SCHEDULE_LANGUAGES.forEach((lang) => {
			const hasTitle = (titleTranslations[lang.code] || '').trim() !== ''
			const hasDescription =
				(descriptionTranslations[lang.code] || '').trim() !== ''
			result[lang.code] = hasTitle || hasDescription
		})
		return result
	}, [titleTranslations, descriptionTranslations])

	const handleSave = async () => {
		if (!hasAtLeastOneTitle) return

		setIsSaving(true)
		try {
			// Filter out empty translations
			const filteredTitleTranslations: Record<string, string> = {}
			const filteredDescriptionTranslations: Record<string, string> = {}

			Object.entries(titleTranslations).forEach(([lang, value]) => {
				if (value.trim()) {
					filteredTitleTranslations[lang] = value.trim()
				}
			})

			Object.entries(descriptionTranslations).forEach(([lang, value]) => {
				if (value.trim()) {
					filteredDescriptionTranslations[lang] = value.trim()
				}
			})

			// For backward compatibility, pass the first available title/description
			const firstTitle =
				titleTranslations['en-US'] ||
				Object.values(filteredTitleTranslations)[0] ||
				''
			const firstDescription =
				descriptionTranslations['en-US'] ||
				Object.values(filteredDescriptionTranslations)[0] ||
				''

			await onSave(
				firstTitle,
				firstDescription,
				filteredTitleTranslations,
				filteredDescriptionTranslations,
				isPublic,
				isPublic ? contentCategoryIds : [],
				resellerId
			)
			onOpenChange(false)
		} finally {
			setIsSaving(false)
		}
	}

	const handleTitleChange = (value: string) => {
		setTitleTranslations((prev) => ({
			...prev,
			[selectedLanguage]: value,
		}))
	}

	const handleDescriptionChange = (value: string) => {
		setDescriptionTranslations((prev) => ({
			...prev,
			[selectedLanguage]: value,
		}))
	}

	const currentTitle = titleTranslations[selectedLanguage] || ''
	const currentDescription = descriptionTranslations[selectedLanguage] || ''

	const toggleCategory = (categoryId: number) => {
		setContentCategoryIds((prev) =>
			prev.includes(categoryId)
				? prev.filter((id) => id !== categoryId)
				: [...prev, categoryId]
		)
	}

	const removeCategory = (categoryId: number, e: React.MouseEvent) => {
		e.stopPropagation()
		setContentCategoryIds((prev) => prev.filter((id) => id !== categoryId))
	}

	const formContent = (
		<>
			{canManagePublic && (
				<div className={isDesktop ? 'px-6 pt-4' : 'px-5 pt-3'}>
					<Tabs
						value={selectedLanguage}
						onValueChange={(value) =>
							setSelectedLanguage(value as ScheduleLanguageCode)
						}
					>
						<TabsList>
							{SUPPORTED_SCHEDULE_LANGUAGES.map((lang) => (
								<TabsTrigger key={lang.code} value={lang.code}>
									{lang.nativeLabel}
									{languageHasContent[lang.code] && (
										<span className="ml-1.5 h-1.5 w-1.5 rounded-full bg-primary" />
									)}
								</TabsTrigger>
							))}
						</TabsList>
					</Tabs>
				</div>
			)}

			<div
				className={
					isDesktop
						? 'mt-2 px-6 pb-6 space-y-2 overflow-y-auto'
						: 'flex flex-col flex-1 min-h-0 mt-2 px-5 pb-4 space-y-2 overflow-y-auto'
				}
			>
				<div className="flex flex-col gap-4 md:block md:space-y-2">
					<Label htmlFor="edit-title" className="text-xs md:text-base">
						{canManagePublic
							? t('entities.schedule.title_with_locale', {
									locale: selectedLanguage,
								})
							: t('entities.schedule.schedule_name_required')}
					</Label>
					<Input
						id="edit-title"
						placeholder={t('entities.schedule.schedule_name')}
						value={currentTitle}
						onChange={(e) => handleTitleChange(e.target.value)}
						className="h-10"
						disabled={isSaving}
					/>
				</div>

				<div className="flex flex-col gap-4 md:block md:space-y-2">
					<Label htmlFor="edit-description" className="text-xs md:text-base">
						{canManagePublic
							? t('entities.schedule.description_with_locale', {
									locale: selectedLanguage,
								})
							: t('entities.schedule.description')}
					</Label>
					<Textarea
						id="edit-description"
						placeholder={t('entities.schedule.description_placeholder')}
						value={currentDescription}
						onChange={(e) => handleDescriptionChange(e.target.value)}
						className="min-h-[120px] bg-background border-border text-foreground resize-none"
						maxLength={descriptionMaxLength}
						disabled={isSaving}
					/>
					<p className="text-sm text-muted-foreground">
						{currentDescription.length}/{descriptionMaxLength}
					</p>
				</div>

				{canManagePublic && (
					<div className="flex flex-col gap-4 md:block md:space-y-4">
						<div className="flex items-center justify-between">
							<div className="space-y-0.5">
								<Label className="text-xs md:text-base">
									{t('entities.schedule.public')}
								</Label>
								<p className="text-sm text-muted-foreground">
									{t('entities.schedule.public_description')}
								</p>
							</div>
							<Switch
								checked={isPublic}
								onCheckedChange={setIsPublic}
								disabled={isSaving}
							/>
						</div>

						<div className="space-y-2">
							<Label className="text-xs md:text-base">
								{t('entities.schedule.reseller_owner')}
							</Label>
							<Popover
								open={isResellerPopoverOpen}
								onOpenChange={(open) => {
									setIsResellerPopoverOpen(open)
									if (!open) setResellerSearch('')
								}}
							>
								<PopoverTrigger asChild>
									<Button
										variant="outline"
										role="combobox"
										aria-expanded={isResellerPopoverOpen}
										className="w-full justify-between h-10 px-3 bg-transparent dark:bg-input/30"
										disabled={isSaving}
									>
										<span
											className={
												resellerId ? undefined : 'text-muted-foreground'
											}
										>
											{selectedReseller?.name ??
												(resellerId
													? `#${resellerId}`
													: t('entities.schedule.reseller_owner_none'))}
										</span>
										<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
									</Button>
								</PopoverTrigger>
								<PopoverContent
									className="w-[var(--radix-popover-trigger-width)] p-0"
									align="start"
								>
									<Command shouldFilter={false}>
										<CommandInput
											placeholder={t(
												'entities.schedule.reseller_owner_placeholder'
											)}
											value={resellerSearch}
											onValueChange={setResellerSearch}
										/>
										<CommandList
											className="max-h-64 overflow-hidden"
											onWheel={(e) => e.stopPropagation()}
										>
											<CommandEmpty>
												{isResellersLoading
													? t('actions.loading')
													: t('entities.schedule.reseller_owner_not_found')}
											</CommandEmpty>
											<ScrollArea className="h-64 overflow-y-auto">
												<CommandGroup>
													{resellerId && (
														<CommandItem
															onSelect={() => {
																setResellerId(null)
																setIsResellerPopoverOpen(false)
															}}
															className="flex items-center gap-2 cursor-pointer text-muted-foreground"
														>
															<X className="h-3 w-3" />
															{t('entities.schedule.reseller_owner_clear')}
														</CommandItem>
													)}
													{resellers.map((reseller) => (
														<CommandItem
															key={reseller.id}
															onSelect={() => {
																setResellerId(reseller.id)
																setIsResellerPopoverOpen(false)
																setResellerSearch('')
															}}
															className="flex items-center gap-2 cursor-pointer"
														>
															<Check
																className={`h-4 w-4 ${
																	resellerId === reseller.id
																		? 'opacity-100'
																		: 'opacity-0'
																}`}
															/>
															{reseller.name}
														</CommandItem>
													))}
												</CommandGroup>
											</ScrollArea>
										</CommandList>
									</Command>
								</PopoverContent>
							</Popover>
							<p className="text-sm text-muted-foreground">
								{t('entities.schedule.reseller_owner_description')}
							</p>
						</div>

						{isPublic && (
							<div className="space-y-2">
								<Label className="text-xs md:text-base">
									{t('entities.schedule.business_categories')}
								</Label>
								<Popover
									open={isCategoryPopoverOpen}
									onOpenChange={setIsCategoryPopoverOpen}
								>
									<PopoverTrigger asChild>
										<Button
											variant="outline"
											role="combobox"
											aria-expanded={isCategoryPopoverOpen}
											className="w-full justify-between h-auto min-h-10 py-2 px-3 bg-transparent dark:bg-input/30"
											disabled={isSaving}
										>
											<div className="flex flex-wrap gap-1 items-center overflow-hidden text-left">
												{contentCategoryIds.length > 0 ? (
													contentCategoryIds.map((id) => {
														const category = contentCategories.find(
															(c) => c.id === id
														)
														return (
															<Badge
																key={id}
																variant="secondary"
																className="mr-1 py-0 px-2 h-6 flex items-center gap-1 bg-primary/20 text-primary border-transparent whitespace-nowrap"
															>
																{category?.getLocalizedName('en-US') || id}
																<X
																	className="h-3 w-3 cursor-pointer hover:text-foreground"
																	onClick={(e) => removeCategory(id, e)}
																/>
															</Badge>
														)
													})
												) : (
													<span className="text-muted-foreground">
														{t('entities.schedule.select_categories')}
													</span>
												)}
											</div>
											<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
										</Button>
									</PopoverTrigger>
									<PopoverContent
										className="w-[var(--radix-popover-trigger-width)] p-0"
										align="start"
									>
										<Command>
											<CommandInput
												placeholder={t('entities.schedule.business_categories')}
											/>
											<CommandList
												className="max-h-64 overflow-hidden"
												onWheel={(e) => e.stopPropagation()}
											>
												<CommandEmpty>
													{t('entities.schedule.no_categories_found')}
												</CommandEmpty>
												<ScrollArea className="h-64 overflow-y-auto">
													<CommandGroup>
														{contentCategories.map((category) => (
															<CommandItem
																key={category.id}
																onSelect={() => toggleCategory(category.id)}
																className={cn(
																	'flex items-center gap-2 cursor-pointer',
																	category.parentId && 'ml-4'
																)}
															>
																<Checkbox
																	checked={contentCategoryIds.includes(
																		category.id
																	)}
																	onCheckedChange={() =>
																		toggleCategory(category.id)
																	}
																	className="pointer-events-none"
																/>
																{category.parentId ? (
																	<Tag className="h-3 w-3 text-muted-foreground" />
																) : (
																	<Folder className="h-3 w-3 text-primary" />
																)}
																<span>
																	{category.getLocalizedName('en-US')}
																</span>
															</CommandItem>
														))}
													</CommandGroup>
												</ScrollArea>
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>
								<p className="text-sm text-muted-foreground">
									{t('entities.schedule.business_categories_description')}
								</p>
							</div>
						)}
					</div>
				)}
			</div>
		</>
	)

	if (isDesktop) {
		return (
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="max-w-md p-0 gap-0 flex flex-col max-h-[90vh]">
					<DialogHeader className="px-6 pt-6 pb-3">
						<DialogTitle className="text-2xl font-semibold">
							{t('entities.schedule.change_information')}
						</DialogTitle>
					</DialogHeader>
					<Separator />
					{formContent}
					<Separator />
					<DialogFooter className="px-6 py-4">
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							className="h-9 rounded-full px-6"
							disabled={isSaving}
						>
							{t('actions.cancel')}
						</Button>
						<Button
							type="button"
							onClick={handleSave}
							disabled={!hasAtLeastOneTitle || isSaving}
							className="h-9 rounded-full px-6"
						>
							{isSaving ? t('entities.schedule.saving') : t('actions.save')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<BottomSheet open={open} onOpenChange={onOpenChange}>
			<BottomSheetContent
				variant="card"
				className="border-border h-[86vh] max-h-[92vh] px-0 flex flex-col overflow-hidden"
			>
				<DialogHeader className="sr-only">
					<DialogTitle>{t('entities.schedule.change_information')}</DialogTitle>
				</DialogHeader>
				<div className="px-5 pt-2 pb-3 flex items-center justify-between">
					<h2 className="md:text-xl text-lg font-semibold">
						{t('entities.schedule.change_information')}
					</h2>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						onClick={() => onOpenChange(false)}
						className="h-9 w-9 rounded-full"
					>
						<X className="w-5 h-5" />
					</Button>
				</div>
				<Separator />
				<div className="flex-1 min-h-0 flex flex-col overflow-hidden">
					{formContent}
				</div>
				<div className="h-24 flex items-center justify-center px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3 border-t border-border bg-card">
					<Button
						type="button"
						onClick={handleSave}
						disabled={!hasAtLeastOneTitle || isSaving}
						className="rounded-full w-full"
					>
						{isSaving ? t('entities.schedule.saving') : t('actions.continue')}
					</Button>
				</div>
			</BottomSheetContent>
		</BottomSheet>
	)
}
